import {
  asDraggableData,
  destinationFor,
  droppableId,
  type DroppableData,
} from "@/dnd/ids";
import { cnTransferDropTargetSurface } from "@/dnd/transferDropTargetStyles";
import { useTransferDropHighlight } from "@/dnd/useTransferDropHighlight";
import { useRailOsDropHighlightId } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/RailFilesDropHighlight";
import { RailOsFileDropZone } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/RailOsFileDropZone";
import { useRailScopeOsDrop } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/useRailScopeOsDrop";
import {
  RAIL_LIST_ITEM_ATTR,
  RAIL_SELECTED_ATTR,
  useRailRowSelection,
  type RailSelectionSource,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import { WorkspacesRailDraggableCanvasRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailDraggableCanvasRow";
import { useCanvasNodesMirrorSynced } from "@/pages/canvas/context/CanvasNodesMirrorContext";
import type { Canvas } from "@cohesive/api-client";
import { useDndContext, useDroppable } from "@dnd-kit/core";
import { useMemo } from "react";

/**
 * The canvas node as a file drop target — ONLY while the canvas is open, so
 * the drop lands visibly: the `canvas-scope` surface routes to the board,
 * which places a node where things can be seen. A closed canvas is not a
 * target (dropping into it would silently copy a file nobody can see —
 * bring files in from the open board instead).
 *
 * It is a FILE-only zone, so it stays **disabled** while dragging a canvas or
 * chat — a file zone must never intercept a canvas/chat drag meant for the
 * section or a folder (dnd-kit skips disabled droppables in collision).
 *
 * Also the contiguous-selection list-item host: adjacent canvas nodes are
 * drop-zone siblings, not row siblings.
 */
function CanvasRowDropZone({
  canvasId,
  canvasOpen,
  section = "canonical",
  children,
}: {
  canvasId: string;
  canvasOpen: boolean;
  section?: RailSelectionSource;
  children: React.ReactNode;
}) {
  const { active } = useDndContext();
  const activeIsFiles =
    !!active && asDraggableData(active.data.current)?.kind === "files";

  const { selected } = useRailRowSelection(canvasId, section, "canvas", false);

  const dropId = droppableId.railCanvasRow(canvasId);
  const droppableData = useMemo(
    (): DroppableData => ({
      surface: "canvas-scope",
      destination: destinationFor("canvas-scope", { canvasId }),
    }),
    [canvasId],
  );
  const droppable = useDroppable({
    id: dropId,
    data: droppableData,
    disabled: !activeIsFiles || !canvasOpen,
  });
  const { isOver, disallowed } = useTransferDropHighlight(
    dropId,
    droppableData.destination,
  );

  const railScopeOsDrop = useRailScopeOsDrop({ canvasId });
  const handleOsDrop = canvasOpen ? railScopeOsDrop : null;
  const osActiveDropId = useRailOsDropHighlightId();
  const osOver = osActiveDropId === dropId;

  return (
    <RailOsFileDropZone
      ref={droppable.setNodeRef}
      dropId={dropId}
      onDrop={handleOsDrop}
      className={cnTransferDropTargetSurface({
        isOver: isOver || osOver,
        disallowed,
      })}
      {...{ [RAIL_LIST_ITEM_ATTR]: "" }}
      {...(selected ? { [RAIL_SELECTED_ATTR]: "" } : {})}
    >
      {children}
    </RailOsFileDropZone>
  );
}

/**
 * A canvas inside the workspace containment tree. The rail shows places, not
 * contents: the row opens the canvas (its files live on the board), it never
 * expands into a file tree. The whole node stays a file drop target — see
 * CanvasRowDropZone above.
 */
export function WorkspacesRailCanvasNode({
  canvas,
  dragNamespace,
  section,
  active,
  onRename,
  onShare,
  onDelete,
  sourcePath,
}: {
  canvas: Canvas;
  dragNamespace: string;
  /** Rail section this node lives in — drives the source-aware highlight. */
  section?: RailSelectionSource;
  active?: boolean;
  onRename: (canvas: Canvas) => void;
  onShare?: (canvas: Canvas) => void;
  onDelete?: (canvas: Canvas) => void;
  /** Parent path of this canvas's row in the workspace tree, where one exists. */
  sourcePath?: string;
}) {
  // The rail shows places, never their contents — a canvas is a row you
  // open, not a tree you expand. Its files live on the canvas.
  const canvasOpen = useCanvasNodesMirrorSynced(canvas.id);

  return (
    <CanvasRowDropZone
      canvasId={canvas.id}
      canvasOpen={canvasOpen}
      section={section}
    >
      <div className="flex flex-col">
        <WorkspacesRailDraggableCanvasRow
          canvas={canvas}
          dragNamespace={dragNamespace}
          section={section}
          active={active}
          onRename={onRename}
          onShare={onShare}
          onDelete={onDelete}
          sourcePath={sourcePath}
        />
      </div>
    </CanvasRowDropZone>
  );
}
